<template>
  <img
    v-if="isShown"
    ref="img"
    class="media"
    :style="style"
    :alt="alt"
    v-bind="attrs"
    @load="onLoad"
  >
</template>

<script>
import sha1 from 'sha1';
import {
  computed, defineComponent, onMounted,
  ref,
} from 'vue';

export const createHashedSrc = (src, width, height) => {
  const filename = src.replace(/^.*[\\/]/, '');
  const hash = sha1(`media_content${width}${height}${filename}sportmaster404`).substring(0, 4);

  return src.replace('{width}', width).replace('{height}', height).replace('{hash}', hash);
};

export default defineComponent({
  name: 'media',
});
</script>

<script setup>
// import sha1 from 'sha1';

const props = defineProps({
  media: {
    type: Object,
    default: () => ({}),
  },
  src: {
    type: String,
    default: '',
  },
  alt: {
    type: String,
    default: '',
  },
  width: {
    type: [String, Number],
    default: null,
  },
  height: {
    type: [String, Number],
    default: null,
  },
  backgroundColor: {
    type: String,
    default: '#e0e0e0',
  },
  notLazy: {
    type: Boolean,
    default: false,
  },
  hasPlaceholder: {
    type: Boolean,
    default: false,
  },
});

const img = ref(null);
const isLoading = ref(!props.notLazy);
const imgInView = ref(false);

const aspectRatio = computed(() => {
  const defaultRatio = 0.7225; // width/height
  if (!props.width || !props.height) return defaultRatio;

  return (props.width / props.height);
});
const computedWidth = computed(() => props.width || Math.round(props.height * aspectRatio.value));
const computedHeight = computed(() => props.height || Math.round(props.width / aspectRatio.value));
const computedSrc = computed(() => props.src || props.media.url);
const hashedSrc = computed(() => createHashedSrc(computedSrc.value, computedWidth.value, computedHeight.value));
const isShown = computed(() => (props.src || props.media.type === 'photo'));

const attrs = computed(() => {
  if (!props.notLazy) {
    return imgInView.value ? { src: hashedSrc.value } : {};
  }
  return { src: hashedSrc.value };
});

onMounted(() => {
  emit('on-mounted');
  const el = img.value;

  if (el instanceof HTMLElement && !props.notLazy) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          imgInView.value = entry.isIntersecting;
          observer.unobserve(el);
        }
      });
    });

    observer.observe(el);
  }
});

// стили для лейзи-лоад
const style = computed(() => {
  if (props.notLazy || !props.width || !props.hasPlaceholder) return;

  const applyAspectRatio = isLoading.value && aspectRatio.value;
  const styles = { backgroundColor: props.backgroundColor };

  if (applyAspectRatio) {
    styles.aspectRatio = aspectRatio.value;
    styles.height = 0;
    styles.paddingTop = `${computedHeight.value}px`;
  }

  return styles;
});

const emit = defineEmits(['on-load', 'on-mounted']);

function onLoad() {
  if (!props.notLazy) {
    isLoading.value = false;
  }
  emit('on-load');
}
</script>

<style lang="scss">
.media {
  display: inline-block;
}
</style>
